import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { format } from 'date-fns';
import { 
  ArrowLeft, 
  Phone, 
  Mail, 
  MapPin, 
  Calendar, 
  ShoppingBag, 
  DollarSign,
  Clock,
  Eye
} from 'lucide-react';
import { ProtectedRoute } from '@/components/protected-route';
import { MainLayout } from '@/components/main-layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCustomer, useCustomerOrders } from '@/hooks/use-customers';
import { InvoiceDetailDialog } from '@/components/orders/invoice-detail-dialog';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { clampPage, resolveTotalItems, resolveTotalPages } from '@/lib/pagination';
import { api } from '@/lib/api';

export const Route = createFileRoute('/customers/$id')({
  component: () => (
    <ProtectedRoute>
      <MainLayout>
        <CustomerDetailPage />
      </MainLayout>
    </ProtectedRoute>
  ),
});

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(amount);
}

function safeFormatDate(dateString: string) {
  try {
    return format(new Date(dateString), 'dd MMM yyyy');
  } catch {
    return '-';
  }
}

function CustomerDetailPage() {
  const { id } = useParams({ from: '/customers/$id' });
  const [page, setPage] = useState(1);
  const [isInvoiceDetailOpen, setIsInvoiceDetailOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  
  const { data: customer, isLoading: isLoadingCustomer, error: customerError } = useCustomer(id as string);
  const { data: ordersResponse, isLoading: isLoadingOrders } = useCustomerOrders(id as string, { page });

  const { data: orderSummary } = useQuery({
    queryKey: ['customers', id, 'orders-summary'],
    enabled: !!id,
    queryFn: async () => {
      const SUMMARY_PAGE_SIZE = 100;
      const MAX_SUMMARY_PAGES = 50;

      let nextPage = 1;
      let resolvedTotalPages = 1;
      let summaryTotalOrders = 0;
      let summaryTotalSpent = 0;
      let summaryLastOrderDate: string | null = null;

      while (nextPage <= resolvedTotalPages && nextPage <= MAX_SUMMARY_PAGES) {
        const response = await api.customers.getOrders(id as string, {
          page: nextPage,
          limit: SUMMARY_PAGE_SIZE,
        });
        const pageOrders = response.data || [];

        for (const order of pageOrders) {
          summaryTotalSpent += order.totalAmount;
          if (!summaryLastOrderDate || new Date(order.createdAt) > new Date(summaryLastOrderDate)) {
            summaryLastOrderDate = order.createdAt;
          }
        }

        if (!response.pagination) {
          summaryTotalOrders = Math.max(summaryTotalOrders, pageOrders.length);
          break;
        }

        const pageTotalItems = resolveTotalItems(response.pagination, pageOrders.length);
        summaryTotalOrders = Math.max(summaryTotalOrders, pageTotalItems, pageOrders.length);
        resolvedTotalPages = resolveTotalPages(response.pagination, pageTotalItems);
        nextPage += 1;
      }

      return {
        totalOrders: summaryTotalOrders,
        totalSpent: summaryTotalSpent,
        lastOrderDate: summaryLastOrderDate,
      };
    },
  });
  
  const orders = ordersResponse?.data || [];
  const pagination = ordersResponse?.pagination;
  const totalItems = resolveTotalItems(pagination, orders.length);
  const totalPages = resolveTotalPages(pagination, orders.length);

  // Calculate statistics
  const fallbackLastOrderDate = orders.reduce<string | null>((latest, order) => {
    if (!latest) return order.createdAt;
    return new Date(order.createdAt) > new Date(latest) ? order.createdAt : latest;
  }, null);
  const totalOrders = customer?.totalOrders ?? orderSummary?.totalOrders ?? totalItems;
  const totalSpent = customer?.totalSpent ?? orderSummary?.totalSpent ?? orders.reduce((sum, order) => sum + order.totalAmount, 0);
  const lastOrderDate = orderSummary?.lastOrderDate ?? fallbackLastOrderDate;

  if (isLoadingCustomer) {
    return (
      <div className="flex justify-center items-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (customerError || !customer) {
    return (
      <div className="text-center py-24 text-destructive">
        <p>Failed to load customer details.</p>
        <Link to="/customers">
          <Button variant="outline" className="mt-4">Back to Customers</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-5xl min-w-0">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-4 w-full">
          <Link to="/customers">
            <Button variant="ghost" size="icon" className="-ml-2">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-3xl font-bold tracking-tight truncate">
              {customer.name}
            </h1>
            <p className="text-muted-foreground text-sm">
              Customer Details
            </p>
          </div>
        </div>
      </div>

      {/* Customer Info Card */}
      <Card>
        <CardHeader>
          <CardTitle>Customer Information</CardTitle>
          <CardDescription>
            Contact details and account information
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Phone className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{customer.phone || '-'}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{customer.email || '-'}</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="font-medium">{customer.address || '-'}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Member Since</p>
                  <p className="font-medium">{safeFormatDate(customer.createdAt)}</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Orders</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-primary" />
              {totalOrders}
            </CardTitle>
          </CardHeader>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Spent</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              {formatCurrency(totalSpent)}
            </CardTitle>
          </CardHeader>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Last Order</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              {lastOrderDate ? safeFormatDate(lastOrderDate) : '-'}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Order History */}
      <Card>
        <CardHeader>
          <CardTitle>Order History</CardTitle>
          <CardDescription>
            All orders placed by this customer
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingOrders ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ShoppingBag className="mx-auto h-12 w-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No orders yet</p>
              <p className="text-sm">This customer hasn't placed any orders.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order #</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow 
                        key={order.id}
                        className="cursor-pointer hover:bg-muted/50 transition-colors group"
                        onClick={() => {
                          setSelectedInvoiceId(order.id);
                          setIsInvoiceDetailOpen(true);
                        }}
                      >
                        <TableCell className="font-black group-hover:text-primary transition-colors">
                          {order.orderNumber}
                        </TableCell>
                        <TableCell>
                          {safeFormatDate(order.orderDate)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(order.totalAmount)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              order.paymentStatus === 'PAID'
                                ? 'default'
                                : order.paymentStatus === 'PARTIAL'
                                  ? 'secondary'
                                  : 'destructive'
                            }
                          >
                            {order.paymentStatus}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              order.orderStatus === 'READY' || order.orderStatus === 'DELIVERED'
                                ? 'default' // Assuming 'default' for success, or 'success' if available
                                : order.orderStatus === 'CANCELLED'
                                  ? 'destructive'
                                  : 'secondary'
                            }
                          >
                            {order.orderStatus === 'READY' || order.orderStatus === 'DELIVERED' ? 'DONE' : order.orderStatus}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            title="View Order"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedInvoiceId(order.id);
                              setIsInvoiceDetailOpen(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {pagination && (
                <div className="mt-4">
                  <PaginationControls
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={(nextPage) => setPage(clampPage(nextPage, totalPages))}
                    totalItems={totalItems}
                    isLoading={isLoadingOrders}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <InvoiceDetailDialog
        open={isInvoiceDetailOpen}
        onOpenChange={setIsInvoiceDetailOpen}
        orderId={selectedInvoiceId}
      />
    </div>
  );
}
